import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Form, Collapse } from 'react-bootstrap'
import styled from 'styled-components'
import { FaChevronRight } from 'react-icons/fa'
import { ModelHyperparameters, ModelParameter } from '../../types/models/model'

interface HyperparameterSettingsModalProps {
  show: boolean
  handleClose: () => void
  onSave: (parameters: any) => void
  initialHyperparameters?: any
}

const StyledModal = styled(Modal)`
  .modal-content {
    background: #2a2a2a;
    color: #ffffff;
  }

  .modal-header {
    border-bottom: 1px solid #404040;
  }

  .modal-footer {
    border-top: 1px solid #404040;
  }

  .form-control {
    background: #1a1a1a;
    border: 1px solid #404040;
    color: #ffffff;

    &:focus {
      background: #1a1a1a;
      border-color: #007bff;
      color: #ffffff;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  .form-check-input {
    background-color: #1a1a1a;
    border-color: #404040;

    &:checked {
      background-color: #007bff;
      border-color: #007bff;
    }
  }

  .form-label {
    color: #ffffff;
  }

  .text-muted {
    color: rgba(255, 255, 255, 0.7) !important;
  }
`

const Section = styled.div`
  margin-bottom: 2rem;
  padding: 1rem;
  background: #1a1a1a;
  border-radius: 8px;

  h4 {
    color: #ffffff;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;

    .chevron {
      margin-right: 0.5rem;
      transition: transform 0.2s ease;

      &.open {
        transform: rotate(90deg);
      }
    }
  }

  .section-content {
    margin-top: 1rem;
  }
`

const SubSection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #2a2a2a;
  border-radius: 6px;

  h5 {
    color: #ffffff;
    margin-bottom: 1rem;
    opacity: 0.8;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;

    .chevron {
      margin-right: 0.5rem;
      transition: transform 0.2s ease;

      &.open {
        transform: rotate(90deg);
      }
    }
  }

  .subsection-content {
    margin-top: 1rem;
  }
`

const HyperparameterSettingsModal: React.FC<HyperparameterSettingsModalProps> = ({
  show,
  handleClose,
  onSave,
  initialHyperparameters = {},
}) => {
  const defaultTrainingParams: ModelHyperparameters = {
    type: 'default',
    hyperparameters: {
      training: {
        epochs: {
          name: 'epochs',
          type: 'static',
          input_type: 'integer',
          value: 10,
          min_value: 1,
          max_value: 100,
        },
        // learning_rate: {
        //   name: 'learning_rate',
        //   type: 'static',
        //   input_type: 'float',
        //   value: 0.001,
        //   min_value: 0.0001,
        //   max_value: 0.1,
        // },
        // batch_size: {
        //   name: 'batch_size',
        //   type: 'static',
        //   input_type: 'integer',
        //   value: 32,
        //   min_value: 1,
        //   max_value: 128,
        // },
      },
      transforms: {},
    },
  }

  const [hyperparameters, setHyperparameters] = useState<ModelHyperparameters | null>(null)
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({
    training: true,
    transforms: false,
  })

  const [openSubSections, setOpenSubSections] = useState<{
    [key: string]: boolean
  }>({})
  // const selectedModelId = useAppSelector(selectSelectedModelId)
  // const { data: defaultHyperparameters } = useGetDefaultHyperparametersQuery(selectedModelId || '', {
  //   skip: !selectedModelId,
  // })

  // Store the initial hyperparameters in a ref to compare changes
  const previousHyperparametersRef = useRef(initialHyperparameters)

  useEffect(() => {
    if (show) {
      // Combine the hyperparameter objects, using defaults if no values are provided
      const combinedHyperparameters: ModelHyperparameters = {
        type: initialHyperparameters?.type || defaultTrainingParams.type,
        hyperparameters: {
          training: {
            ...defaultTrainingParams.hyperparameters.training,
            ...(initialHyperparameters?.hyperparameters?.training || {}),
          },
          transforms: {
            ...(initialHyperparameters?.hyperparameters?.transforms || {}),
          },
        },
      }

      setHyperparameters(combinedHyperparameters)
    }
  }, [show, initialHyperparameters])

  // Reset states when modal is closed
  useEffect(() => {
    if (!show) {
      setHyperparameters(null)
      previousHyperparametersRef.current = {}
    }
  }, [show])

  // const handleInputChange = (
  //   section: 'training' | 'transforms',
  //   transformName: string | null,
  //   paramName: string,
  //   value: any
  // ) => {
  //   setHyperparameters((prev: ModelHyperparameters | null) => {
  //     if (!prev) return prev

  //     const newHyperparameters = {
  //       type: prev.type,
  //       hyperparameters: {
  //         training: { ...prev.hyperparameters.training },
  //         transforms: { ...prev.hyperparameters.transforms },
  //       },
  //     }

  //     if (section === 'training') {
  //       newHyperparameters.hyperparameters.training[paramName] = {
  //         ...prev.hyperparameters.training[paramName],
  //         value: value,
  //       }
  //     } else if (section === 'transforms' && transformName) {
  //       if (!newHyperparameters.hyperparameters.transforms[transformName]) {
  //         newHyperparameters.hyperparameters.transforms[transformName] = {}
  //       }
  //       newHyperparameters.hyperparameters.transforms[transformName] = {
  //         ...prev.hyperparameters.transforms[transformName],
  //       }
  //       newHyperparameters.hyperparameters.transforms[transformName][paramName] = {
  //         ...prev.hyperparameters.transforms[transformName][paramName],
  //         value: value,
  //       }
  //     }

  //     return newHyperparameters
  //   })
  // }

  const renderParameter = (
    param: ModelParameter,
    section: 'training' | 'transforms',
    transformName: string | null
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = param.input_type === 'boolean' ? e.target.checked : e.target.value
      let finalValue: number | boolean | string = value

      if (param.input_type === 'integer') {
        finalValue = parseInt(value as string, 10)
        if (param.min_value !== undefined && finalValue < param.min_value) {
          finalValue = param.min_value
        }
        if (param.max_value !== undefined && finalValue > param.max_value) {
          finalValue = param.max_value
        }
      } else if (param.input_type === 'float') {
        finalValue = parseFloat(value as string)
        if (param.min_value !== undefined && finalValue < param.min_value) {
          finalValue = param.min_value
        }
        if (param.max_value !== undefined && finalValue > param.max_value) {
          finalValue = param.max_value
        }
      }

      setHyperparameters((prev) => {
        if (!prev) return prev

        const newHyperparameters = { ...prev }
        if (section === 'training') {
          newHyperparameters.hyperparameters.training[param.name] = {
            ...param,
            value: finalValue,
          }
        } else if (transformName) {
          newHyperparameters.hyperparameters.transforms[transformName][param.name] = {
            ...param,
            value: finalValue,
          }
        }
        return newHyperparameters
      })
    }

    return (
      <Form.Group key={param.name} className="mb-3">
        <Form.Label className="d-flex justify-content-between align-items-center">
          <span>
            {param.name
              ?.split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')}
          </span>
          {param.min_value !== undefined && param.max_value !== undefined && (
            <small className="text-muted">
              ({param.min_value} - {param.max_value})
            </small>
          )}
        </Form.Label>
        {param.input_type === 'boolean' ? (
          <Form.Check type="switch" checked={param.value as boolean} onChange={handleChange} />
        ) : (
          <Form.Control
            type={param.input_type === 'integer' ? 'number' : 'text'}
            value={String(param.value)}
            onChange={handleChange}
            min={param.min_value}
            max={param.max_value}
            step={param.input_type === 'float' ? 0.001 : 1}
          />
        )}
      </Form.Group>
    )
  }

  const handleSave = () => {
    if (hyperparameters) {
      onSave({
        hyperparameters: hyperparameters,
      })
    }
    handleClose()
  }

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const toggleSubSection = (transformName: string) => {
    setOpenSubSections((prev) => ({
      ...prev,
      [transformName]: !prev[transformName],
    }))
  }

  return (
    <StyledModal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Model Hyperparameters {hyperparameters?.type ? `- ${hyperparameters.type}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {hyperparameters?.hyperparameters?.training &&
            Object.keys(hyperparameters.hyperparameters.training).length > 0 && (
              <Section key="training-section">
                <h4 onClick={() => toggleSection('training')}>
                  <FaChevronRight className={`chevron ${openSections.training ? 'open' : ''}`} />
                  Training Parameters
                </h4>
                <Collapse in={openSections.training}>
                  <div className="section-content">
                    {Object.entries(hyperparameters.hyperparameters.training)?.map(
                      ([key, param]) => (
                        <div key={`training-${key}`}>
                          {renderParameter(param, 'training', null)}
                        </div>
                      )
                    )}
                  </div>
                </Collapse>
              </Section>
            )}

          {hyperparameters?.hyperparameters?.transforms &&
            Object.keys(hyperparameters.hyperparameters.transforms).length > 0 && (
              <Section key="transforms-section">
                <h4 onClick={() => toggleSection('transforms')}>
                  <FaChevronRight className={`chevron ${openSections.transforms ? 'open' : ''}`} />
                  Transform Parameters
                </h4>
                <Collapse in={openSections.transforms}>
                  <div className="section-content">
                    {Object.entries(hyperparameters.hyperparameters.transforms)?.map(
                      ([transformName, params]) => (
                        <SubSection key={`transform-${transformName}`}>
                          <h5 onClick={() => toggleSubSection(transformName)}>
                            <FaChevronRight
                              className={`chevron ${openSubSections[transformName] ? 'open' : ''}`}
                            />
                            {(transformName?.split('_') || [])
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')}
                          </h5>
                          <Collapse in={openSubSections[transformName]}>
                            <div className="subsection-content">
                              {Object.entries(params || {}).map(([key, param]) => (
                                <div key={`transform-${transformName}-${key}`}>
                                  {renderParameter(param, 'transforms', transformName)}
                                </div>
                              ))}
                            </div>
                          </Collapse>
                        </SubSection>
                      )
                    )}
                  </div>
                </Collapse>
              </Section>
            )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </Modal.Footer>
    </StyledModal>
  )
}

export default HyperparameterSettingsModal
